// Pantalla del paciente: lista sus sesiones 'completada' ordenadas por
// ended_at desc. Sin click handler en items (MVP). El detalle por sesión
// queda para una iteración siguiente cuando agreguemos useSesionDetalle
// y resolvamos el caveat de SesionCompletada de paso.

import { Link } from "react-router";
import { ChevronLeft, Clock } from "lucide-react";
import {
  useHistorialSesiones,
  type SesionHistoricaRow,
} from "@/lib/supabase/queries/useHistorialSesiones";
import { PacienteBottomNav } from "@/app/components/paciente/PacienteBottomNav";
import { PacienteSidebar } from "@/app/components/paciente/PacienteSidebar";
import { PacienteHeaderDesktop } from "@/app/components/paciente/PacienteHeaderDesktop";
import { formatFechaRelativa } from "@/lib/utils";

export function HistorialSesiones() {
  const { data, error, loading } = useHistorialSesiones();

  return (
    <div className="min-h-screen w-full flex flex-col bg-background transition-colors duration-200">
      <PacienteHeaderDesktop />

      <div className="flex-1 flex justify-center lg:justify-start">
        <PacienteSidebar />

        <div className="w-full max-w-[480px] lg:max-w-3xl flex flex-col pb-24 lg:pb-8 lg:flex-1 lg:min-w-0">
          <div className="p-6 lg:px-8 lg:pt-6 space-y-6">
            {/* Header mobile con ChevronLeft. Desktop lo cubre PacienteHeaderDesktop. */}
            <div className="flex items-center gap-4 lg:hidden">
              <Link
                to="/paciente-home"
                className="transition-all duration-200 ease-out hover:opacity-70"
              >
                <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
              </Link>
              <h1 className="text-2xl">Tu historial</h1>
            </div>
            <h1 className="hidden lg:block text-2xl font-bold text-primary">
              Tu historial
            </h1>

            {loading && <LoadingState />}
            {error && <ErrorState message={error} />}
            {!loading && !error && data && data.length === 0 && <EmptyState />}
            {!loading && !error && data && data.length > 0 && (
              <SessionList sesiones={data} />
            )}
          </div>
        </div>
      </div>

      <PacienteBottomNav />
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <p className="text-center text-muted-foreground py-12">
      Cargando tu historial…
    </p>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-card rounded-2xl p-6 border border-border text-center">
      <p className="text-orange-600 font-semibold mb-2">
        No pudimos cargar tu historial
      </p>
      <p className="text-sm text-muted-foreground mb-4">{message}</p>
      <Link
        to="/paciente-home"
        className="inline-block bg-primary text-primary-foreground px-5 py-2 rounded-full text-sm font-semibold"
      >
        Volver al inicio
      </Link>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-card rounded-3xl p-8 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] text-center">
      <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto mb-4">
        <Clock className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <h2 className="text-lg font-semibold mb-2">
        Todavía no completaste ninguna sesión
      </h2>
      <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
        Cuando hagas tu primera, va a aparecer acá con tus reps y tu calidad.
      </p>
      <Link
        to="/paciente-home"
        className="inline-block bg-primary text-primary-foreground px-5 py-3 rounded-full text-sm font-semibold transition-colors hover:bg-primary/90"
      >
        Empezar mi rutina
      </Link>
    </div>
  );
}

function SessionList({ sesiones }: { sesiones: SesionHistoricaRow[] }) {
  return (
    <div className="space-y-3">
      {sesiones.map((s) => (
        <SessionCard key={s.id} sesion={s} />
      ))}
    </div>
  );
}

function SessionCard({ sesion }: { sesion: SesionHistoricaRow }) {
  const fechaLabel = formatFechaRelativa(sesion.ended_at);
  const duracionLabel =
    sesion.duration_sec !== null ? formatDuration(sesion.duration_sec) : null;
  const qualityPct =
    sesion.score_promedio !== null
      ? Math.round(sesion.score_promedio * 100)
      : null;
  const qualityClass = qualityColorClass(qualityPct);

  return (
    <div className="bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground mb-1">{fechaLabel}</p>
          <p className="font-semibold text-primary truncate">
            {sesion.ejercicio_nombre}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {sesion.total_reps} reps
            {duracionLabel && ` · ${duracionLabel}`}
          </p>
        </div>
        <div className={`text-2xl font-[800] tabular-nums ${qualityClass}`}>
          {qualityPct !== null ? `${qualityPct}%` : "—"}
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/** sec → "mm:ss". */
function formatDuration(sec: number): string {
  const mm = Math.floor(sec / 60);
  const ss = Math.floor(sec % 60);
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

/** Color del % de calidad por rango. < 40% usa orange (no red) — en
 *  contexto clínico el rojo connota peligro/emergencia, una sesión con
 *  calidad baja es atención al detalle, no alerta médica. */
function qualityColorClass(pct: number | null): string {
  if (pct === null) return "text-muted-foreground";
  if (pct >= 70) return "text-success";
  if (pct >= 40) return "text-amber-600";
  return "text-orange-600";
}
